import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  DropdownOption,
  SearchableDropdownComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";
import { useAppDispatch } from "../../state/hooks";
import { fetchLookup } from "../../state/slices/lookupsSlice";
import { getAccountMasterKeyFromConfig } from "../../utils/lookupUtils";
import { QuickAddModal } from "./QuickAddModal";

interface SearchableDropdownProps {
  config: SearchableDropdownComponent;
  value?: string | number | boolean | (string | number | boolean)[];
  onChange?: (
    name: string,
    value: string | number | boolean | (string | number | boolean)[]
  ) => void;
  onSearch?: (searchTerm: string) => void;
  onFocus?: () => void;
  externalError?: string;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  config,
  value,
  onChange,
  onSearch,
  onFocus,
  externalError,
}) => {
  const dispatch = useAppDispatch();
  const instanceId =
    (config as SearchableDropdownComponent & { dropdownInstanceId?: string })
      .dropdownInstanceId ||
    config.BindValue ||
    config.name ||
    "dropdown";

  // DEBUGGING LOGS - moved after getDisplayValue definition

  const [isOpen, setIsOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const options = Array.isArray(config.options) ? config.options : [];

  const isDisabled = Boolean(
    config.disabled || (config as SearchableDropdownComponent & { IsReadOnly?: boolean }).IsReadOnly
  );

  const isOptionDisabled = (option: { disabled?: boolean }) => Boolean(option?.disabled);

  const findNextSelectableIndex = (
    startIndex: number,
    direction: 1 | -1,
    list: DropdownOption[]
  ): number => {
    if (list.length === 0) return -1;
    let index = startIndex;
    for (let i = 0; i < list.length; i++) {
      if (index < 0) index = direction === 1 ? 0 : list.length - 1;
      else if (index >= list.length) index = direction === 1 ? 0 : list.length - 1;
      if (!isOptionDisabled(list[index])) return index;
      index += direction;
    }
    return -1;
  };

  /* ------------------------------ VALIDATION ------------------------------ */

  const validateField = (inputValue: any): string => {
    if (!config.validation) return "";
    for (const rule of config.validation) {
      if (
        rule.rule === ValidationRule.REQUIRED &&
        (inputValue === undefined ||
          inputValue === "" ||
          (Array.isArray(inputValue) && inputValue.length === 0))
      ) {
        return rule.message || "Please select an option";
      }
    }
    return "";
  };

  /* ------------------------------ VALUE MATCHING ------------------------------ */

  const matchValue = (
    optionValue: string | number | boolean,
    searchValue?: string | number | boolean
  ): boolean => {
    if (searchValue === undefined || searchValue === null) return false;

    if (optionValue === searchValue) return true;
    if (String(optionValue) === String(searchValue)) return true;

    const optNum = Number(optionValue);
    const searchNum = Number(searchValue);
    if (!isNaN(optNum) && !isNaN(searchNum) && optNum === searchNum) return true;

    if (typeof optionValue === "boolean") {
      if (searchValue === "true") return optionValue === true;
      if (searchValue === "false") return optionValue === false;
    }

    if (typeof searchValue === "boolean") {
      if (optionValue === "true") return searchValue === true;
      if (optionValue === "false") return searchValue === false;
    }

    return false;
  };

  /* ------------------------------ FILTER ------------------------------ */

  const filteredOptions = options.filter((opt) =>
    (opt?.label ?? opt?.value ?? "").toString().toLowerCase().includes((searchTerm || "").toLowerCase())
  );

  // Reset option refs array when filtered options change
  useEffect(() => {
    optionRefs.current = new Array(filteredOptions.length).fill(null);
  }, [filteredOptions.length]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length && filteredOptions.length > 0) {
      setHighlightedIndex(0);
    } else if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(-1);
    }
  }, [filteredOptions.length, highlightedIndex]);

  // Initialize highlighted index when dropdown opens
  useEffect(() => {
    if (isOpen && filteredOptions.length > 0 && highlightedIndex === -1) {
      // If there's a current value, highlight it; otherwise highlight first selectable option
      if (!config.multiple && value !== undefined && value !== "") {
        const currentIndex = filteredOptions.findIndex((opt) =>
          matchValue(opt.value, value as any)
        );
        if (currentIndex >= 0 && !isOptionDisabled(filteredOptions[currentIndex])) {
          setHighlightedIndex(currentIndex);
          setTimeout(() => {
            const optionElement = optionRefs.current[currentIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
          }, 50);
        } else {
          setHighlightedIndex(findNextSelectableIndex(0, 1, filteredOptions));
        }
      } else {
        setHighlightedIndex(findNextSelectableIndex(0, 1, filteredOptions));
      }
    } else if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen, filteredOptions.length, value, config.multiple]);

  /* ------------------------------ AUTO FIX (LABEL → VALUE) ------------------------------ */

  const autoFixedRef = useRef(false);

  useEffect(() => {
    if (
      autoFixedRef.current ||
      config.multiple ||
      !value ||
      options.length === 0
    )
      return;

    // Check if value matches any option by value or id
    const valueMatches = options.some((opt) => 
      matchValue(opt.value, value as any) || 
      ((opt as any).id !== undefined && matchValue((opt as any).id, value as any))
    );
    
    if (valueMatches) return;

    const labelMatch = options.find(
      (opt) => (opt?.label ?? "").toString().toLowerCase() === String(value ?? "").toLowerCase()
    );

    if (labelMatch) {
      autoFixedRef.current = true;
      onChange?.(config.name, labelMatch.value);
    }
  }, [value, options]);

  /* ------------------------------ DISPLAY ------------------------------ */

  const getDisplayValue = () => {
    const displayFallback = (config as any).displayFallback;
    // When options empty or no match: use displayFallback or raw value so prepopulated value doesn't disappear
    if (options.length === 0) {
      if (value !== undefined && value !== null && value !== "") {
        if (displayFallback != null && displayFallback !== "") return String(displayFallback);
        return String(value);
      }
      return "";
    }

    if (config.multiple && Array.isArray(value)) {
      return value
        .map((v) => {
          // Try matching by value first
          let opt = options.find((opt) => matchValue(opt.value, v));
          // If not found, try matching by id
          if (!opt) {
            opt = options.find((opt: any) => matchValue(opt.id, v));
          }
          return opt?.label;
        })
        .filter(Boolean)
        .join(", ");
    }

    // Try matching by value first
    let selectedOption = options.find((opt) =>
      matchValue(opt.value, value as any)
    );
    
    // If not found, try matching by id (for cases where value is an ID but opt.value is something else)
    if (!selectedOption && value !== undefined && value !== null && value !== "") {
      selectedOption = options.find((opt: any) => 
        opt.id !== undefined && matchValue(opt.id, value as any)
      );
    }

    if (selectedOption) return selectedOption.label ?? "";
    // No match: use displayFallback or value so prepopulated Customer Name doesn't disappear
    if (value !== undefined && value !== null && value !== "") {
      if (displayFallback != null && displayFallback !== "") return String(displayFallback);
      return String(value);
    }
    return "";
  };

  // DEBUGGING LOGS for Estimation No field
  useEffect(() => {
    if (config.name === "parentid" || config.name === "parentid1" || config.BindValue === "parentid" || config.BindValue === "parentid1" || config.label === "Estimation No.") {
      const displayVal = getDisplayValue();
      const hasMatchByValue = options.some((opt) => matchValue(opt.value, value as any));
      const hasMatchById = options.some((opt: any) => opt.id !== undefined && matchValue(opt.id, value as any));
      console.log(`[SearchableDropdown DEBUG] ${config.label || config.name} (${config.name || config.BindValue})`, {
        value,
        optionsCount: options.length,
        sampleOptions: options.slice(0, 3), // First 3 options for debugging
        lookupName: config.lookupName,
        displayValue: displayVal,
        hasMatchByValue,
        hasMatchById,
      });
    }
  }, [value, options, config.name, config.label, config.BindValue, config.lookupName]);

  const hasValue = config.multiple
    ? Array.isArray(value) && value.length > 0
    : value !== undefined && value !== "";

  const handleQuickAddSuccess = (response: any) => {
    if (config.lookupName) {
      const accountMasterKey = getAccountMasterKeyFromConfig(config);
      dispatch(
        fetchLookup({
          lookupName: config.lookupName,
          params: {
            parentid: "",
            lookuptype: "Search_Select",
            search_arg: "",
            ...(accountMasterKey ? { accountMasterKey } : {}),
          },
        })
      );
    }

    // Auto-select the new item
    const newId = response?.data?.id || response?.id || response?.data?.uuid || response?.uuid;
    if (newId) {
      onChange?.(config.name, newId);
    }
  };


  /* ------------------------------ HANDLERS ------------------------------ */

  const handleOptionClick = (optionValue: any, optionDisabled?: boolean) => {
    if (isDisabled || optionDisabled) return;

    if (config.multiple) {
      const currentValue = Array.isArray(value) ? value : [];
      let newValue: (string | number | boolean)[];

      if (currentValue.includes(optionValue)) {
        newValue = currentValue.filter((v) => v !== optionValue);
      } else {
        if (
          config.maxSelections &&
          currentValue.length >= config.maxSelections
        ) {
          return;
        }
        newValue = [...currentValue, optionValue];
      }

      const err = validateField(newValue);
      setError(err);
      onChange?.(config.name, newValue);
    } else {
      const err = validateField(optionValue);
      setError(err);
      onChange?.(config.name, optionValue);
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isDisabled) return;

    // Handle Tab key - allow normal tab navigation, just close dropdown
    if (e.key === "Tab") {
      if (isOpen) {
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
      }
      // Don't prevent default - let Tab work naturally
      return;
    }

    // Handle Escape key
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
      inputRef.current?.blur();
      return;
    }

    // Handle Enter key
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        const option = filteredOptions[highlightedIndex];
        if (!isOptionDisabled(option)) {
          handleOptionClick(option.value);
        }
      } else if (!isOpen) {
        // Open dropdown if closed
        setIsOpen(true);
        if (filteredOptions.length > 0) {
          setHighlightedIndex(findNextSelectableIndex(0, 1, filteredOptions));
        }
      }
      return;
    }

    // Handle ArrowDown key
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();

      // Ensure input stays focused
      if (inputRef.current) {
        inputRef.current.focus();
      }

      if (!isOpen) {
        // Open dropdown and highlight first option
        setIsOpen(true);
        setTimeout(() => {
          if (filteredOptions.length > 0) {
            setHighlightedIndex(findNextSelectableIndex(0, 1, filteredOptions));
          }
        }, 10);
      } else {
        // Navigate to next selectable option
        setHighlightedIndex((prev) => {
          const nextIndex = findNextSelectableIndex(
            prev < filteredOptions.length - 1 ? prev + 1 : 0,
            1,
            filteredOptions
          );
          setTimeout(() => {
            const optionElement = optionRefs.current[nextIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
          }, 0);
          return nextIndex;
        });
      }
      return;
    }

    // Handle ArrowUp key
    if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();

      // Ensure input stays focused
      if (inputRef.current) {
        inputRef.current.focus();
      }

      if (!isOpen) {
        // Open dropdown and highlight last option
        setIsOpen(true);
        setTimeout(() => {
          if (filteredOptions.length > 0) {
            setHighlightedIndex(
              findNextSelectableIndex(filteredOptions.length - 1, -1, filteredOptions)
            );
          }
        }, 10);
      } else {
        // Navigate to previous selectable option
        setHighlightedIndex((prev) => {
          const nextIndex = findNextSelectableIndex(
            prev > 0 ? prev - 1 : filteredOptions.length - 1,
            -1,
            filteredOptions
          );
          setTimeout(() => {
            const optionElement = optionRefs.current[nextIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
          }, 0);
          return nextIndex;
        });
      }
      return;
    }
  };

  const handleClear = () => {
    if (isDisabled) return;
    onChange?.(config.name, config.multiple ? [] : "");
    setError("");
    setSearchTerm("");
  };

  useEffect(() => {
    if (isDisabled && isOpen) {
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  }, [isDisabled, isOpen]);

  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  /* ------------------------------ EFFECTS ------------------------------ */

  // Add capture phase listener to intercept arrow keys before global handler
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleCapture = (e: KeyboardEvent) => {
      // Only intercept arrow keys when they're on our searchable dropdown input
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const target = e.target as HTMLElement;
        // Check if target is our input or has the searchable dropdown attribute
        if (target === input || (target instanceof HTMLInputElement && target.getAttribute('data-searchable-dropdown') === 'true')) {
          // Mark the event so global handler knows to skip it
          (e as any).__searchableDropdownHandled = true;
          // Don't stop propagation - let React's handler run, but global handler will skip it
        }
      }
    };

    // Use capture phase on window to run before global handler (which is in bubble phase)
    window.addEventListener("keydown", handleCapture, true);

    return () => {
      window.removeEventListener("keydown", handleCapture, true);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!target) return;

      // Check if click is outside all dropdown elements
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);
      const isOutsideMenu = !dropdownMenuRef.current || !dropdownMenuRef.current.contains(target);
      const isOutsideInput = !inputRef.current || !inputRef.current.contains(target);

      // Only close if click is outside all elements
      if (isOutsideDropdown && isOutsideMenu && isOutsideInput) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    // Use capture phase to catch clicks before they bubble
    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [isOpen]);

  /* ------------------------------ RENDER ------------------------------ */

  return (
    <div className="relative w-full px-1" ref={dropdownRef} data-field-name={config.name}>
      {config.label && (
        <label className="block mb-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <div className="flex gap-2 items-center">
        <div className="relative flex-1 flex items-center">
          <input
            ref={inputRef}
            type="text"
            placeholder={hasValue ? "" : config.placeholder || "Search..."}
            value={isOpen ? searchTerm : ""}
            data-searchable-dropdown="true"
            data-dropdown-open={isOpen ? "true" : "false"}
            role="combobox"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-controls={isOpen ? `dropdown-${instanceId}` : undefined}
            aria-activedescendant={
              isOpen && highlightedIndex >= 0
                ? `option-${instanceId}-${highlightedIndex}`
                : undefined
            }
            onChange={(e) => {
              setSearchTerm(e.target.value);
              onSearch?.(e.target.value);
              // Reset highlighted index when search term changes
              setHighlightedIndex(-1);
            }}
            onFocus={() => {
              if (isDisabled) return;
              setIsOpen(true);
              setSearchTerm("");
              setHighlightedIndex(-1);
              onFocus?.();
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              // No longer needed due to onMouseDown preventDefault fix
            }}
            disabled={isDisabled}
            readOnly={isDisabled}
            className={`w-full py-2 px-3 text-sm font-normal text-left outline-none rounded-md font-sans dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${isDisabled ? "cursor-not-allowed opacity-70 bg-gray-100 dark:bg-gray-700" : "cursor-text"} ${error
              ? "border border-danger-500 dark:border-danger-500"
              : (config.isSubModule && config.required && !isDisabled && !hasValue)
                ? "border border-danger-500 dark:border-danger-400 ring-2 ring-danger-500/40 dark:ring-danger-400/40 focus:border-danger-600 dark:focus:border-danger-500 focus-visible:ring-danger-600 dark:focus-visible:ring-danger-500"
                : isOpen
                  ? "border border-primary-500 dark:border-primary-400"
                  : "border border-gray-300 dark:border-gray-600"
              } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
          />

          {hasValue && !isOpen && options.length > 0 && (
            <div className="absolute left-4 right-12 top-1/2 -translate-y-1/2 pointer-events-none text-sm font-normal text-gray-900 dark:text-gray-100 overflow-hidden text-ellipsis whitespace-nowrap">
              {getDisplayValue()}
            </div>
          )}

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {config.clearable && hasValue && !isDisabled && (
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button
              type="button"
              disabled={isDisabled}
              className={`p-1 transition-colors ${isDisabled ? "text-gray-300 dark:text-gray-600 cursor-not-allowed" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"}`}
              onMouseDown={(e) => {
                if (isDisabled) return;
                e.preventDefault(); // Prevent input from losing focus
                e.stopPropagation();
              }}
              onClick={(e) => {
                if (isDisabled) return;
                e.preventDefault();
                e.stopPropagation();
                const willOpen = !isOpen;
                setIsOpen(willOpen);

                if (willOpen) {
                  // When opening, initialize highlighted index
                  setTimeout(() => {
                    inputRef.current?.focus();
                    if (filteredOptions.length > 0) {
                      if (!config.multiple && value !== undefined && value !== "") {
                        const currentIndex = filteredOptions.findIndex((opt) =>
                          matchValue(opt.value, value as any)
                        );
                        const startIndex =
                          currentIndex >= 0 && !isOptionDisabled(filteredOptions[currentIndex])
                            ? currentIndex
                            : 0;
                        setHighlightedIndex(findNextSelectableIndex(startIndex, 1, filteredOptions));
                      } else {
                        setHighlightedIndex(findNextSelectableIndex(0, 1, filteredOptions));
                      }
                    }
                  }, 0);
                } else {
                  setHighlightedIndex(-1);
                }
              }}
            >
              <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {config.PopupScreenId && !isDisabled && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsQuickAddOpen(true);
            }}
            className="p-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-colors h-[38px] w-[38px] flex items-center justify-center shadow-sm"
            title="Add New"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            id={`dropdown-${instanceId}`}
            role="listbox"
            className="dark:bg-gray-800 !w-fit border border-gray-300 dark:border-gray-600 max-h-[400px] overflow-y-auto z-[9999] shadow-md bg-gray-50"
            style={{
              position: "absolute",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {filteredOptions.length === 0 ? (
              <div className="py-2 px-3 text-sm font-normal text-gray-500 dark:text-gray-400 italic">No options found</div>
            ) : (
              filteredOptions.map((option, idx) => {
                const optionDisabled = isOptionDisabled(option);
                return (
                <div
                  key={idx}
                  id={`option-${instanceId}-${idx}`}
                  ref={(el) => {
                    optionRefs.current[idx] = el;
                  }}
                  role="option"
                  aria-selected={highlightedIndex === idx || matchValue(option.value, value as any)}
                  aria-disabled={optionDisabled}
                  tabIndex={-1}
                  className={`py-2 px-3 text-sm font-normal text-left font-sans border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors duration-200 ${
                    optionDisabled
                      ? "cursor-not-allowed opacity-50 text-gray-400 dark:text-gray-500"
                      : "cursor-pointer text-gray-900 dark:text-gray-100"
                  } ${
                    !optionDisabled &&
                    (highlightedIndex === idx
                      ? "bg-primary-200 dark:bg-primary-800/70 text-primary-900 dark:text-primary-100 font-medium"
                      : matchValue(option.value, value as any)
                        ? "bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700")
                  }`}
                  onMouseEnter={() => {
                    if (!optionDisabled) setHighlightedIndex(idx);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input from losing focus
                    if (!optionDisabled) {
                      handleOptionClick(option.value);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span>{option.label}</span>
                    {config.multiple &&
                      Array.isArray(value) &&
                      value.includes(option.value) && (
                        <span className="text-success-600 dark:text-success-400 font-bold">
                          ✓
                        </span>
                      )}
                  </div>
                </div>
              );
              })
            )}
          </div>,
          document.body
        )}

      {(error || externalError) && (
        <div className="text-danger-500 dark:text-danger-400 text-xs mt-1">{error || externalError}</div>
      )}
      {config.PopupScreenId && (
        <QuickAddModal
          isOpen={isQuickAddOpen}
          popupScreenId={config.PopupScreenId}
          onClose={() => setIsQuickAddOpen(false)}
          onSuccess={handleQuickAddSuccess}
        />
      )}
    </div>
  );
};
